import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import { ChevronLeft, Truck, Shield, Leaf, Star } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProductDetailPage = () => {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedState, setSelectedState] = useState('California');
  const [quantity, setQuantity] = useState(1);
  const [reviews, setReviews] = useState({ reviews: [], count: 0, average_rating: 0 });
  const { addToCart } = useCart();

  useEffect(() => {
    axios.get(`${API}/products/${slug}`).then(r => { setProduct(r.data); setLoading(false); }).catch(() => setLoading(false));
    axios.get(`${API}/reviews/${slug}`).then(r => setReviews(r.data)).catch(() => {});
  }, [slug]);

  const handleAdd = () => {
    addToCart(product, selectedState, quantity);
    toast.success('Added to bag', { description: `${quantity} x ${product.name} — ${selectedState}` });
  };

  if (loading) return (
    <div className="min-h-screen pt-32 pb-24">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-2 gap-16">
        <div className="aspect-square bg-bb-rich animate-pulse" />
        <div className="space-y-6"><div className="h-6 bg-bb-rich w-1/4 animate-pulse" /><div className="h-12 bg-bb-rich w-3/4 animate-pulse" /></div>
      </div>
    </div>
  );

  if (!product) return (
    <div className="min-h-screen pt-32 pb-24 text-center">
      <p className="font-heading text-3xl c-cream">Product not found</p>
      <Link to="/products" className="t-label c-gold mt-4 inline-block">Back to Shop</Link>
    </div>
  );

  const parts = product.description.split('\n\n');

  return (
    <div className="min-h-screen pt-28" data-testid="product-detail-page">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 mb-8">
        <Link to="/products" className="t-label c-muted hover:c-cream transition-colors inline-flex items-center gap-2" data-testid="back-to-products">
          <ChevronLeft className="w-3 h-3" /> Back to Shop
        </Link>
      </div>

      <section className="max-w-7xl mx-auto px-6 lg:px-12 pb-24">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
          <div className="aspect-square overflow-hidden bg-bb-rich border border-bb-border">
            <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" />
          </div>
          <div className="lg:py-4">
            <span className="t-label c-gold block mb-3">{product.category}</span>
            <h1 className="t-headline text-4xl md:text-5xl c-cream mb-4">{product.name}</h1>
            <p className="font-heading text-3xl c-gold mb-6">${product.price.toFixed(2)}</p>
            <div className="gold-line mb-6" />
            <p className="t-body c-muted text-base mb-8">{product.short_description}</p>

            <div className="mb-8">
              <p className="t-label c-muted mb-3">Select Origin</p>
              <div className="flex gap-4">
                {product.state_options.map(s => (
                  <button key={s} onClick={() => setSelectedState(s)}
                    className={`flex items-center gap-3 px-5 py-3 border transition-all ${selectedState === s ? 'border-bb-gold bg-bb-gold/10 c-cream' : 'border-bb-dim c-muted hover:border-bb-gold'}`}
                    data-testid={`select-state-${s.toLowerCase()}`}
                  >
                    <span className={s === 'California' ? 'dot-ca' : 'dot-az'} />
                    <span className="t-label text-[11px]">{s}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-8">
              <p className="t-label c-muted mb-3">Quantity</p>
              <div className="flex items-center gap-4">
                <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="w-10 h-10 border border-bb-dim flex items-center justify-center c-muted hover:border-bb-gold transition-colors text-lg" data-testid="decrease-quantity">&minus;</button>
                <span className="font-heading text-xl c-cream w-10 text-center">{quantity}</span>
                <button onClick={() => setQuantity(quantity + 1)} className="w-10 h-10 border border-bb-dim flex items-center justify-center c-muted hover:border-bb-gold transition-colors text-lg" data-testid="increase-quantity">+</button>
              </div>
            </div>

            <button onClick={handleAdd} className="btn-primary w-full mb-8" data-testid="add-to-cart-detail">
              Add to Bag — ${(product.price * quantity).toFixed(2)}
            </button>

            <div className="grid grid-cols-3 gap-4 py-6 border-t border-bb-border">
              {[
                { icon: <Truck className="w-4 h-4" />, text: 'Free Shipping $50+' },
                { icon: <Shield className="w-4 h-4" />, text: 'Secure Payment' },
                { icon: <Leaf className="w-4 h-4" />, text: '100% Natural' },
              ].map((b, i) => (
                <div key={i} className="text-center">
                  <span className="c-gold block mb-2 flex justify-center">{b.icon}</span>
                  <span className="t-label text-[9px] c-muted">{b.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-20 pt-16 border-t border-bb-border max-w-3xl mx-auto">
          <h2 className="font-heading text-3xl c-cream text-center mb-10">About This Product</h2>
          <div className="prose-luxury">
            {parts.map((part, i) => {
              const boldify = (text) => {
                const segs = text.split(/\*\*(.*?)\*\*/g);
                return segs.map((seg, j) => j % 2 === 1 ? <strong key={j} className="c-cream font-medium">{seg}</strong> : seg);
              };
              if (part.startsWith('**') && part.endsWith('**')) return <h3 key={i} className="font-heading text-xl c-cream mt-8 mb-3 italic">{part.replace(/\*\*/g, '')}</h3>;
              if (part.startsWith('- ')) {
                const items = part.split('\n').filter(l => l.startsWith('- '));
                return <ul key={i} className="space-y-2 mb-6 ml-4">{items.map((item, j) => <li key={j} className="t-body text-sm c-muted pl-5 relative"><span className="absolute left-0 top-2.5 w-1.5 h-1.5 bg-bb-gold rounded-full" />{boldify(item.replace('- ', ''))}</li>)}</ul>;
              }
              return <p key={i} className="t-body c-muted text-sm mb-4">{boldify(part)}</p>;
            })}
          </div>
        </div>

        {/* Reviews Section */}
        {reviews.count > 0 && (
          <div className="mt-20 pt-16 border-t border-bb-border max-w-3xl mx-auto" data-testid="reviews-section">
            <div className="text-center mb-10">
              <h2 className="font-heading text-3xl c-cream mb-4">Customer Reviews</h2>
              <div className="flex items-center justify-center gap-3 mb-2">
                <div className="flex gap-1">
                  {[1,2,3,4,5].map(s => (
                    <Star key={s} className={`w-4 h-4 ${s <= Math.round(reviews.average_rating) ? 'text-bb-gold fill-bb-gold' : 'text-bb-dim'}`} />
                  ))}
                </div>
                <span className="font-heading text-xl c-cream">{reviews.average_rating}</span>
                <span className="t-label text-[10px] c-muted">({reviews.count} reviews)</span>
              </div>
            </div>
            <div className="space-y-6">
              {reviews.reviews.map(r => (
                <div key={r.id} className="bg-bb-rich border border-bb-border p-6" data-testid={`review-${r.id}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <div className="flex gap-1 mb-1">
                        {[1,2,3,4,5].map(s => (
                          <Star key={s} className={`w-3 h-3 ${s <= r.rating ? 'text-bb-gold fill-bb-gold' : 'text-bb-dim'}`} />
                        ))}
                      </div>
                      <span className="t-body text-sm c-cream font-medium">{r.name}</span>
                      <span className="t-body text-xs c-muted ml-2">{r.location}</span>
                    </div>
                    <div className="text-right">
                      <span className="t-label text-[9px] c-muted">{r.date}</span>
                      {r.verified && <span className="t-label text-[9px] c-gold block">Verified Purchase</span>}
                    </div>
                  </div>
                  <h4 className="t-body text-sm c-cream font-medium mb-2">{r.title}</h4>
                  <p className="t-body text-sm c-muted">{r.body}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Product Schema with Reviews */}
        {product && (
          <script type="application/ld+json" dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Product",
              "name": product.name,
              "description": product.short_description,
              "image": product.image_url,
              "brand": {"@type": "Brand", "name": "Bee Boosted"},
              "url": `https://beeboosted.com/products/${product.slug}`,
              "offers": {
                "@type": "Offer",
                "price": product.price,
                "priceCurrency": "USD",
                "availability": "https://schema.org/InStock",
                "url": `https://beeboosted.com/products/${product.slug}`,
                "seller": {"@type": "Organization", "name": "Bee Boosted"},
                "shippingDetails": {
                  "@type": "OfferShippingDetails",
                  "shippingRate": {"@type": "MonetaryAmount", "value": "0", "currency": "USD"},
                  "shippingDestination": {"@type": "DefinedRegion", "addressCountry": "US"},
                  "deliveryTime": {
                    "@type": "ShippingDeliveryTime",
                    "handlingTime": {"@type": "QuantitativeValue", "minValue": 1, "maxValue": 3, "unitCode": "DAY"},
                    "transitTime": {"@type": "QuantitativeValue", "minValue": 3, "maxValue": 7, "unitCode": "DAY"}
                  }
                },
                "hasMerchantReturnPolicy": {
                  "@type": "MerchantReturnPolicy",
                  "applicableCountry": "US",
                  "returnPolicyCategory": "https://schema.org/MerchantReturnFiniteReturnWindow",
                  "merchantReturnDays": 30,
                  "returnMethod": "https://schema.org/ReturnByMail",
                  "returnFees": "https://schema.org/FreeReturn"
                }
              },
              ...(reviews.count > 0 ? {
                "aggregateRating": {
                  "@type": "AggregateRating",
                  "ratingValue": reviews.average_rating,
                  "bestRating": "5",
                  "reviewCount": reviews.count
                },
                "review": reviews.reviews.map(r => ({
                  "@type": "Review",
                  "author": {"@type": "Person", "name": r.name},
                  "datePublished": r.date,
                  "reviewRating": {"@type": "Rating", "ratingValue": r.rating, "bestRating": "5"},
                  "name": r.title,
                  "reviewBody": r.body
                }))
              } : {})
            })
          }} />
        )}
      </section>
    </div>
  );
};

export default ProductDetailPage;
